import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { Form, Spacer, TextField, InternalPage } from 'theSource/components';
import { TypographyStyled } from './ChangePasswordFormStyled';
import {
  CHANGE_PASSWORD_INITIAL_VALUES,
  CHANGE_PASSWORD_QUERY_KEY,
  getValidationSchema,
} from './utils';
import { useApiMutation } from 'theSource/utils';
import {
  ChangePasswordErrorDto,
  ChangePasswordFormValues,
  ChangePasswordRequestDto,
  ChangePasswordResponseDto,
} from './types';
import { GET_CONTENT_SETTINGS_QUERY_KEY } from 'theSource/queries';
import { ROUTES } from 'theSource/app/routes';
import {
  selectUserName,
  selectToken,
} from 'theSource/features/ForgotPassword/services';

export const ChangePasswordForm = () => {
  const stateUsername = useSelector(selectUserName);
  const params = new URLSearchParams(window.location.search);
  const paramsUsername = params.get('username');
  const userName = stateUsername || paramsUsername;
  const getTokenFromQuery = () => {
    return params.get('token');
  };
  let token = useSelector(selectToken);

  if (!token) {
    token = getTokenFromQuery();
  }

  const { t } = useTranslation('pages.forgotPassword');

  const navigate = useNavigate();

  const { isLoading, mutate: changePassword } = useApiMutation<
    ChangePasswordResponseDto,
    ChangePasswordErrorDto,
    ChangePasswordRequestDto
  >({
    queryKey: CHANGE_PASSWORD_QUERY_KEY,
    method: 'put',
    endpoint: '/update_password',
    queryKeysToInvalidate: GET_CONTENT_SETTINGS_QUERY_KEY,
    getErrorMessage: (error) => error.response?.data.details,
    getSuccessMessage: () => t('passwordSuccessfullyChanged'),
    snackbarOptions: {
      position: 'bottom',
    },
    onSuccess: () => navigate(ROUTES.public.login.path),
  });

  const handleSubmit = (values: ChangePasswordFormValues) =>
    changePassword({
      token: token,
      password: values.newPassword,
      confirm_password: values.confirmPassword,
    });

  return (
    <InternalPage title={t('changePasswordTitle')} withBackLink>
      <Form
        validationSchema={getValidationSchema()}
        onSubmitClick={handleSubmit}
        initialValues={CHANGE_PASSWORD_INITIAL_VALUES}
        isLoading={isLoading}
        enableDirtyFormHandling={false}
        submitButtonLabel={t('changePassword')}
        submitButtonUseFullWidth
        testId="change-password-form"
      >
        <Box>
          <TypographyStyled variant="body1">
            {t('changingPasswordFor')}
          </TypographyStyled>
          <h2 className="headline-3">{userName}</h2>
        </Box>
        <div className="mt-2">
          <p>
            <span className="red">*</span> = {t('common:required')}
          </p>
        </div>
        <TextField
          fullWidth
          required
          type="password"
          id="newPassword"
          name="newPassword"
          autocomplete="new-password"
          label={t('newPassword')}
        />
        <TextField
          fullWidth
          required
          type="password"
          id="confirmPassword"
          name="confirmPassword"
          autocomplete="new-password"
          label={t('confirmPassword')}
        />
        <Spacer />
      </Form>
    </InternalPage>
  );
};
